import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { CommunityListItem, Post, Topic, UserListItem } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { compact, plural } from "../../lib/format";
import { CommunityIcon } from "../../components/ui/Avatar";
import { IconButton } from "../../components/ui/Button";
import { Skeleton } from "../../components/ui/Skeleton";
import { Tooltip } from "../../components/ui/Tooltip";
import { toast } from "../../components/ui/Toast";
import { IconAdd, IconChevronRight, IconHash, IconSearch, IconX } from "../../components/icons";
import { useAuth, useAuthGate } from "../auth/AuthProvider";
import { UserRow } from "../users/UserRow";
import { CommunityJoinButton, useJoinCommunity } from "../communities/CommunityJoinButton";
import { PanelSection } from "./PanelSection";

export interface DiscoverData {
  popularCommunities: CommunityListItem[];
  recommendedCommunities: CommunityListItem[];
  trendingPosts: Post[];
  popularCreators: UserListItem[];
  suggestedUsers: UserListItem[];
  topics: Topic[];
}

export const useDiscover = () => useQuery({ queryKey: ["discover"], queryFn: () => api.get<DiscoverData>("/api/discover"), staleTime: 120_000 });

/**
 * Search field. `value`/`onChange` make it controlled (Explore); otherwise it
 * submits to /explore?q=.
 */
export function SearchBox({
  initial = "",
  autoFocus,
  value,
  onChange,
  placeholder = "Search",
  size = "md",
}: {
  initial?: string;
  autoFocus?: boolean;
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  size?: "md" | "lg";
}) {
  const [own, setOwn] = useState(initial);
  const q = value ?? own;
  const set = onChange ?? setOwn;
  const navigate = useNavigate();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!onChange && q.trim()) navigate(`/explore?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form onSubmit={submit} role="search" className="relative">
      <IconSearch className="icon pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-fg-3" aria-hidden />
      <input
        value={q}
        onChange={(e) => set(e.target.value)}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label="Search"
        enterKeyHint="search"
        className={cn(
          "w-full rounded-lg border border-line bg-bg pl-9 pr-9 text-fg transition-colors placeholder:text-fg-3 hover:border-line-strong focus:border-fg-3 focus:outline-none",
          size === "lg" ? "h-10 text-base" : "h-9 text-sm",
        )}
      />
      {q && (
        <button type="button" onClick={() => set("")} aria-label="Clear search" className="absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-fg-3 hover:bg-surface-2 hover:text-fg">
          <IconX className="icon size-4" aria-hidden />
        </button>
      )}
    </form>
  );
}

function topicMeta(t: Topic) {
  return [t.postCount ? plural(t.postCount, "post") : null, t.communityCount ? plural(t.communityCount, "community", "communities") : null].filter(Boolean).join(" · ");
}

/** A topic. `panel` renders the dense side-panel row. */
export function TopicRow({ t, panel }: { t: Topic; panel?: boolean }) {
  if (panel)
    return (
      <Link to={`/t/${t.topic}`} aria-label={`${t.topic}: ${topicMeta(t)}`} className="flex h-10 items-center gap-3 rounded-lg px-2 transition-colors hover:bg-surface">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-surface-2 text-fg-3">
          <IconHash className="icon size-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-fg-2">{t.topic}</span>
        {t.postCount > 0 && <span className="text-xs text-fg-3 tabular">{compact(t.postCount)}</span>}
      </Link>
    );
  return (
    <Link to={`/t/${t.topic}`} className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-surface/60">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-surface-2 text-fg-3">
        <IconHash className="icon size-[18px]" aria-hidden />
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-fg">{t.topic}</div>
        <div className="truncate text-xs text-fg-3 tabular">{topicMeta(t)}</div>
      </div>
    </Link>
  );
}

function QuietJoin({ c }: { c: CommunityListItem }) {
  const join = useJoinCommunity();
  const gate = useAuthGate();
  const [pending, setPending] = useState(false);
  if (c.isMember || c.visibility === "private") return null;
  return (
    <IconButton
      size="sm"
      label={`Join ${c.name}`}
      icon={IconAdd}
      disabled={pending}
      onClick={gate(async () => {
        setPending(true);
        try {
          await join(c.id, { open: true });
        } catch (err) {
          toast.error(errorMessage(err, "Couldn't join"));
        } finally {
          setPending(false);
        }
      })}
    />
  );
}

/** A community. `panel` renders the dense side-panel row with a quiet join control. */
export function CommunityRow({ c, panel }: { c: CommunityListItem; panel?: boolean }) {
  if (panel)
    return (
      <div className="flex h-11 items-center gap-1 rounded-lg pl-2 pr-1 transition-colors hover:bg-surface">
        <Link to={c.isMember ? `/c/${c.id}` : `/community/${c.slug}`} className="flex min-w-0 flex-1 items-center gap-3">
          <CommunityIcon src={c.iconUrl} name={c.name} size="sm" />
          <span className="min-w-0 flex-1 truncate text-sm text-fg-2">{c.name}</span>
          <Tooltip label={plural(c.memberCount, "member")}>
            <span className="shrink-0 text-xs text-fg-3 tabular">{compact(c.memberCount)}</span>
          </Tooltip>
        </Link>
        <QuietJoin c={c} />
      </div>
    );
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-surface/60">
      <Link to={`/community/${c.slug}`} className="flex min-w-0 flex-1 items-center gap-3">
        <CommunityIcon src={c.iconUrl} name={c.name} size="md" />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-fg">{c.name}</div>
          <div className="truncate text-xs text-fg-3 tabular">
            {plural(c.memberCount, "member")}
            {c.topics[0] && ` · #${c.topics[0]}`}
          </div>
        </div>
      </Link>
      <CommunityJoinButton community={c} size="sm" />
    </div>
  );
}

function More({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} aria-label={label}>
      <IconButton size="sm" label={label} icon={IconChevronRight} tooltip={false} />
    </Link>
  );
}

/** Right panel on wide screens: where people discover — topics, communities and people. */
export function DiscoverSidebar({ search = true }: { search?: boolean }) {
  const { data, isPending } = useDiscover();
  const { me } = useAuth();
  const communities = data ? (data.recommendedCommunities.length ? data.recommendedCommunities : data.popularCommunities.filter((c) => !c.isMember)) : [];
  const people = data ? (me && data.suggestedUsers.length ? data.suggestedUsers : data.popularCreators.filter((u) => !u.isFollowing && u.id !== me?.id)) : [];
  return (
    <div>
      {search && (
        <div className="px-4 pb-1 pt-4">
          <SearchBox />
        </div>
      )}
      {isPending ? (
        <PanelSection title="Discover">
          <div className="space-y-2 px-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        </PanelSection>
      ) : data ? (
        <div className="divide-y divide-line">
          {data.topics.length > 0 && (
            <PanelSection title="Topics" action={<More to="/explore" label="Explore topics" />}>
              {data.topics.slice(0, 5).map((t) => (
                <TopicRow key={t.topic} t={t} panel />
              ))}
            </PanelSection>
          )}
          {communities.length > 0 && (
            <PanelSection title={data.recommendedCommunities.length ? "Communities for you" : "Popular communities"} action={<More to="/explore?tab=communities" label="Browse communities" />}>
              {communities.slice(0, 5).map((c) => (
                <CommunityRow key={c.id} c={c} panel />
              ))}
            </PanelSection>
          )}
          {people.length > 0 && (
            <PanelSection title={me && data.suggestedUsers.length ? "People you may know" : "Popular people"}>
              {people.slice(0, 4).map((u) => (
                <UserRow key={u.id} user={u} panel />
              ))}
            </PanelSection>
          )}
        </div>
      ) : null}
    </div>
  );
}
